// Catalogue hooks — wraps brand/model CRUD queries and mutations.
// Query keys are imported from import/hooks.ts to guarantee key-tuple parity.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { keys } from "../import/hooks";
import {
  getBrands,
  getBrandModels,
  createBrand,
  createModel,
  updateBrand,
  deleteBrand,
  updateModel,
  deleteModel,
  getColors,
  createColor,
  updateColor,
  deleteColor,
} from "./api";
import type { LoanCatalogueType } from "./types";

// Re-export read hooks so CatalogueScreen imports from one place.
export { useBrands, useBrandModels } from "../import/hooks";

// ── Read queries ──────────────────────────────────────────────────────────────

/** All brands for a given type. Always enabled on the catalogue page. */
export function useCatalogueBrands(type: LoanCatalogueType) {
  return useQuery({
    queryKey: keys.brands(type),
    queryFn: () => getBrands(type),
    staleTime: 60 * 1000,
  });
}

/** Models under one brand; only fetches when brandId is non-null. */
export function useCatalogueModels(
  type: LoanCatalogueType,
  brandId: number | null,
) {
  return useQuery({
    queryKey: keys.brandModels(type, brandId),
    queryFn: () => getBrandModels(type, brandId!),
    enabled: brandId !== null,
    staleTime: 60 * 1000,
  });
}

// ── Create mutations ──────────────────────────────────────────────────────────

/** Add a brand; refreshes the brand list on success. */
export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, name }: { type: LoanCatalogueType; name: string }) =>
      createBrand(type, name),
    onSuccess: (_data, { type }) => {
      qc.invalidateQueries({ queryKey: keys.brands(type) });
    },
  });
}

/** Add a model; refreshes that brand's model list on success. */
export function useCreateModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      type,
      brandId,
      name,
    }: {
      type: LoanCatalogueType;
      brandId: number;
      name: string;
    }) => createModel(type, brandId, name),
    onSuccess: (_data, { type, brandId }) => {
      qc.invalidateQueries({ queryKey: keys.brandModels(type, brandId) });
    },
  });
}

// ── Rename mutations ──────────────────────────────────────────────────────────

/** Rename a brand; refreshes the brand list (canonical strings on child models may change). */
export function useUpdateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, id, name }: { type: LoanCatalogueType; id: number; name: string }) =>
      updateBrand(type, id, name),
    onSuccess: (_data, { type }) => {
      // Invalidate brands list; also invalidate all brand-model entries for this type
      // because the canonical strings include the brand name.
      qc.invalidateQueries({ queryKey: keys.brands(type) });
      qc.invalidateQueries({ queryKey: ["support", "agreements", "brand-models", type] });
    },
  });
}

/** Rename a model; refreshes the model list for that brand. */
export function useUpdateModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      type,
      id,
      brandId: _brandId,
      name,
    }: {
      type: LoanCatalogueType;
      id: number;
      brandId: number;
      name: string;
    }) => updateModel(type, id, name),
    onSuccess: (_data, { type, brandId }) => {
      qc.invalidateQueries({ queryKey: keys.brandModels(type, brandId) });
    },
  });
}

// ── Delete mutations ──────────────────────────────────────────────────────────

/** Delete a brand (cascade-hides its models). Refreshes brand list. */
export function useDeleteBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, id }: { type: LoanCatalogueType; id: number }) =>
      deleteBrand(type, id),
    onSuccess: (_data, { type }) => {
      qc.invalidateQueries({ queryKey: keys.brands(type) });
      qc.invalidateQueries({ queryKey: ["support", "agreements", "brand-models", type] });
    },
  });
}

/** Delete a model; refreshes that brand's model list. */
export function useDeleteModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      type,
      id,
      brandId: _brandId,
    }: {
      type: LoanCatalogueType;
      id: number;
      brandId: number;
    }) => deleteModel(type, id),
    onSuccess: (_data, { type, brandId }) => {
      qc.invalidateQueries({ queryKey: keys.brandModels(type, brandId) });
    },
  });
}

// ── Color hooks — global (no type scope) ──────────────────────────────────────
// Query key matches import/hooks useColorOptions so both caches sync on mutations.

const COLOR_KEY = ["support", "agreements", "colors"] as const;

/** All colors — used by catalogue and import drawer (same cache key). */
export function useColors() {
  return useQuery({
    queryKey: COLOR_KEY,
    queryFn: getColors,
    staleTime: 60 * 1000,
  });
}

/** Add a color; invalidates the color list cache. */
export function useCreateColor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, name_ru }: { name: string; name_ru?: string }) =>
      createColor(name, name_ru),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COLOR_KEY });
    },
  });
}

/** Rename a color; invalidates the color list cache. */
export function useUpdateColor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, name_ru }: { id: number; name: string; name_ru?: string }) =>
      updateColor(id, name, name_ru),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COLOR_KEY });
    },
  });
}

/** Delete a color; invalidates the color list cache. */
export function useDeleteColor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteColor(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COLOR_KEY });
    },
  });
}
